import { cn } from '../../utils/formatter'
import { NavLink, type NavLinkProps } from 'react-router-dom'
import React, { useState, createContext, useContext } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { GlassHoverEffect } from '../GlassHoverEffect'
import { SidebarSettings } from './SidebarSettings'
import { usePerformance } from '../../contexts/PerformanceContext'
import { useSidebarBehavior } from '../../contexts/SidebarBehaviorContext'
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext'
import {
  HiOutlineHome,
  HiOutlineClipboard,
  HiOutlineUserGroup,
  //HiCubeTransparent,
  HiBars3,
  HiXMark,
  HiOutlineDocumentText,
  HiOutlineCalendarDays,
  HiOutlineCommandLine,
  HiOutlineNewspaper,
} from 'react-icons/hi2'

import HermesLogo from '../../assets/hermeslogo.svg'

import { IoAddCircleOutline } from 'react-icons/io5'
//import { Key, Users } from 'lucide-react'

/**
 * Configurações de animação da sidebar
 *
 * Para ajustar o comportamento da sidebar, modifique os valores abaixo:
 *
 * EXEMPLOS DE CONFIGURAÇÕES POPULARES:
 *
 * 🚀 RÁPIDA E RESPONSIVA:
 * closeDelay: 300, sidebarDuration: 0.2, contentDuration: 0.15
 *
 * 🎨 SUAVE E ELEGANTE (ATUAL):
 * closeDelay: 800, sidebarDuration: 0.4, contentDuration: 0.3
 *
 * 🐌 LENTA E DRAMÁTICA:
 * closeDelay: 1200, sidebarDuration: 0.6, contentDuration: 0.5
 *
 * ⚡ INSTANTÂNEA:
 * closeDelay: 100, sidebarDuration: 0.1, contentDuration: 0.05
 *
 * 🏢 CORPORATIVA:
 * closeDelay: 600, sidebarDuration: 0.3, contentDuration: 0.2
 */
/**
 * Retorna configurações de animação baseadas no modo de performance
 */
const getSidebarAnimationConfig = (performanceMode: 'performance' | 'beauty') => {
  if (performanceMode === 'performance') {
    return {
      closeDelay: 0,
      sidebarDuration: 0.01,
      contentDuration: 0.01,
      delays: { text: 0, footer: 0, version: 0 },
      easing: 'linear' as const,
    }
  }

  return {
    closeDelay: 400,
    sidebarDuration: 0.5,
    contentDuration: 0.3,
    delays: {
      text: 0.05,
      footer: 0.1,
      version: 0.15,
    },
    easing: 'easeOut' as const,
  }
}

interface Links {
  label: string
  href: string
  icon: React.JSX.Element | React.ReactNode
  shortcut?: string
}

interface Separator {
  type: 'separator'
  label: string
}

type SidebarItem = Links | Separator

interface SidebarContextProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  animate: boolean
}

const SidebarContext = createContext<SidebarContextProps | undefined>(undefined)

export const useSidebar = () => {
  const context = useContext(SidebarContext)
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider')
  }
  return context
}

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) => {
  const [openState, setOpenState] = useState(false)

  const open = openProp !== undefined ? openProp : openState
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate }}>{children}</SidebarContext.Provider>
  )
}

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate,
}: {
  children: React.ReactNode
  open?: boolean
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>
  animate?: boolean
}) => {
  return (
    <SidebarProvider open={open ?? false} setOpen={setOpen ?? (() => {})} animate={animate ?? true}>
      {children}
    </SidebarProvider>
  )
}

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...(props as any)} />
    </>
  )
}

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate } = useSidebar()
  const { performanceMode } = usePerformance()
  const { sidebarBehavior } = useSidebarBehavior()
  const SIDEBAR_ANIMATION_CONFIG = getSidebarAnimationConfig(performanceMode)
  const [timeoutId, setTimeoutId] = React.useState<ReturnType<typeof setTimeout> | null>(null)

  // Forçar estado baseado no comportamento
  React.useEffect(() => {
    if (sidebarBehavior === 'open') {
      setOpen(true)
    } else if (sidebarBehavior === 'closed') {
      setOpen(false)
    }
  }, [sidebarBehavior, setOpen])

  const handleMouseEnter = () => {
    // Só funciona no modo 'auto'
    if (sidebarBehavior !== 'auto') return

    // Cancelar qualquer timeout pendente
    if (timeoutId) {
      clearTimeout(timeoutId)
      setTimeoutId(null)
    }
    setOpen(true)
  }

  const handleMouseLeave = () => {
    // Só funciona no modo 'auto'
    if (sidebarBehavior !== 'auto') return

    // Adicionar delay configurável antes de fechar
    const id = setTimeout(() => {
      setOpen(false)
      setTimeoutId(null)
    }, SIDEBAR_ANIMATION_CONFIG.closeDelay)
    setTimeoutId(id)
  }

  // Limpar timeout quando componente for desmontado
  React.useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [timeoutId])

  return (
    <motion.div
      className={cn(
        'h-full px-4 py-4 pl-5 hidden md:flex md:flex-col bg-transparent w-[280px] flex-shrink-0 overflow-visible',
        className
      )}
      animate={{
        width: animate ? (open ? '280px' : '70px') : '280px',
      }}
      transition={{
        duration: SIDEBAR_ANIMATION_CONFIG.sidebarDuration,
        ease: [0.23, 1, 0.32, 1], // Curva cubic-bezier mais suave
        type: 'spring', // Força uso de tween ao invés de spring
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      {children}
    </motion.div>
  )
}

export const MobileSidebar = ({ className, children, ...props }: React.ComponentProps<'div'>) => {
  const { open, setOpen } = useSidebar()
  return (
    <>
      <div
        className={cn(
          'h-16 px-4 py-4 flex flex-row md:hidden items-center justify-between bg-transparent w-full'
        )}
        {...props}
      >
        <div className='flex justify-end z-20 w-full'>
          <HiBars3
            className='text-neutral-800 dark:text-neutral-200 cursor-pointer h-6 w-6'
            onClick={() => setOpen(!open)}
          />
        </div>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ x: '-100%', opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: '-100%', opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
              }}
              className={cn(
                'fixed h-full w-full inset-0 bg-gradient-to-br from-blue-900/10 via-sky-500/8 to-blue-600/12 dark:from-blue-900/20 dark:via-sky-500/15 dark:to-blue-600/25 backdrop-blur-md p-10 z-[100] flex flex-col justify-between',
                className
              )}
            >
              <div
                className='absolute right-10 top-10 z-50 text-neutral-800 dark:text-neutral-200 cursor-pointer'
                onClick={() => setOpen(!open)}
              >
                <HiXMark className='h-6 w-6' />
              </div>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  )
}

// export const SidebarSeparator = ({ label }: { label: string }) => {
//   const { open, animate } = useSidebar()
//   const { performanceMode } = usePerformance()
//   const SIDEBAR_ANIMATION_CONFIG = getSidebarAnimationConfig(performanceMode)

//   return (
//     <div className="relative flex items-center py-4">
//       {open ? (
//         <motion.div
//           animate={{
//             opacity: animate ? (open ? 1 : 0) : 1,
//           }}
//           transition={{
//             duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
//             ease: [0.23, 1, 0.32, 1],
//             delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.text : 0,
//           }}
//           className="flex items-center gap-2 w-full px-3"
//         >
//           <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white dark:via-gray-600 to-transparent" />
//           <span className="text-[10px] font-semibold uppercase tracking-wider text-white dark:text-gray-400 whitespace-nowrap">
//             {label}
//           </span>
//           <div className="h-px flex-1 bg-gradient-to-r from-transparent via-white dark:via-gray-600 to-transparent" />
//         </motion.div>
//       ) : (
//         <div className="w-full px-3">
//           <div className="h-px bg-gray-300 dark:bg-gray-600" />
//         </div>
//       )}
//     </div>
//   )
// }

export const SidebarLink = ({
  link,
  className,
  itemProps,
  ...props
}: {
  link: Links
  className?: string
  itemProps?: { ref: (el: HTMLElement | null) => void }
  props?: NavLinkProps
}) => {
  const { open, animate } = useSidebar()
  const { performanceMode } = usePerformance()
  const SIDEBAR_ANIMATION_CONFIG = getSidebarAnimationConfig(performanceMode)

  // Build tooltip text with shortcut if available
  const tooltipText = link.shortcut ? `${link.label} (${link.shortcut})` : link.label

  return (
    <NavLink
      to={link.href}
      end={link.href === '/demands'}
      title={tooltipText}
      {...itemProps}
      className={({ isActive }) =>
        cn(
          'relative flex items-center group/sidebar py-2.5 rounded-xl transition-all duration-300 overflow-hidden min-h-[44px] z-10',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:focus-visible:ring-blue-400/40',
          open ? 'justify-start gap-3' : 'justify-center',
          isActive
            ? 'text-blue-700 dark:text-blue-600 scale-105 font-semibold'
            : 'text-gray-800 dark:text-gray-200 hover:text-gray-900 dark:hover:text-gray-100 scale-100',
          className
        )
      }
      style={({ isActive }) =>
        isActive
          ? {
              textShadow: '0 2px 8px rgba(22, 65, 148, 0.3)',
              filter: 'drop-shadow(0 4px 6px rgba(22, 65, 148, 0.15))',
            }
          : {}
      }
      {...props}
    >
      {({ isActive }) => (
        <>
          {/* Ícone com cores dinâmicas */}
          <div
            className={cn(
              'flex-shrink-0 relative z-10 transition-all duration-300 flex items-center justify-center',
              open ? 'ml-3' : 'mx-auto',
              isActive
                ? 'text-blue-700 dark:text-blue-600 drop-shadow-md scale-110 size-6 [&>svg]:stroke-[2.5] filter brightness-110 contrast-125'
                : 'text-gray-800 dark:text-gray-200 group-hover/sidebar:scale-105 size-5 [&>svg]:stroke-[1.5] drop-shadow'
            )}
          >
            {link.icon}
          </div>

          {/* Texto - apenas quando aberta */}
          {open && (
            <motion.span
              animate={{
                display: animate ? (open ? 'inline-block' : 'none') : 'inline-block',
                opacity: animate ? (open ? 1 : 0) : 1,
              }}
              transition={{
                duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
                ease: [0.23, 1, 0.32, 1],
                delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.text : 0,
                type: 'tween',
              }}
              className={cn(
                'text-sm group-hover/sidebar:translate-x-1 transition-all duration-300 whitespace-pre !p-0 !m-0 font-medium relative z-10 mr-3 flex items-center',
                isActive
                  ? 'text-blue-700 dark:text-blue-600 font-bold drop-shadow-md brightness-110 contrast-125'
                  : 'text-gray-800 dark:text-gray-200'
              )}
            >
              {link.label}
            </motion.span>
          )}
        </>
      )}
    </NavLink>
  )
}

// Logo components
export const Logo = () => {
  const { open, animate } = useSidebar()
  const { performanceMode } = usePerformance()
  const SIDEBAR_ANIMATION_CONFIG = getSidebarAnimationConfig(performanceMode)

  return (
    <NavLink
      to='/'
      className='font-normal flex items-center justify-center text-sm text-black dark:text-white py-2 relative z-20 h-30 overflow-visible'
    >
      {/* Ícone pequeno - visível quando fechada */}
      <motion.div
        animate={{
          display: animate ? (open ? 'none' : 'flex') : 'none',
          opacity: animate ? (open ? 0 : 1) : 1,
        }}
        transition={{
          duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
          ease: [0.23, 1, 0.32, 1],
          type: 'tween',
        }}
        className='relative flex-shrink-0 overflow-visible items-center justify-center'
      >
        <img src={HermesLogo} alt='Logo FIEAC' className='size-8 object-contain overflow-visible' />
      </motion.div>

      {/* Logo completo - visível quando aberta */}
      <motion.div
        animate={{
          display: animate ? (open ? 'flex' : 'none') : 'flex',
          opacity: animate ? (open ? 1 : 0) : 1,
        }}
        transition={{
          duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
          ease: [0.23, 1, 0.32, 1],
          delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.text : 0,
          type: 'tween',
        }}
        className='relative flex-shrink-0 items-center justify-center'
      >
        <img src={HermesLogo} alt='Logo FIEAC' className='size-30 object-contain' />
      </motion.div>
    </NavLink>
  )
}

export const LogoIcon = () => {
  return (
    <NavLink
      to='/'
      className='font-normal flex items-center justify-center text-sm text-black dark:text-white py-2 relative z-20 overflow-visible'
    >
      <div className='relative flex-shrink-0 overflow-visible'>
        <div className='flex gap-1 overflow-visible'>
          <img
            src={HermesLogo}
            alt='Logo FIEAC'
            className='size-8 object-contain overflow-visible'
          />
        </div>
      </div>
    </NavLink>
  )
}

// Main Sidebar Component
export const MainSidebar = () => {
  const [open, setOpen] = useState(false)
  const location = useLocation()
  const { performanceMode } = usePerformance()
  const { sidebarBehavior } = useSidebarBehavior()
  const SIDEBAR_ANIMATION_CONFIG = getSidebarAnimationConfig(performanceMode)

  const { showHelpModal } = useKeyboardShortcutsContext()

  // Sincronizar estado local com o comportamento da sidebar
  React.useEffect(() => {
    if (sidebarBehavior === 'open') {
      setOpen(true)
    } else if (sidebarBehavior === 'closed') {
      setOpen(false)
    }
    // No modo 'auto', mantém o estado atual (controlado pelo hover)
  }, [sidebarBehavior])

  // Build links array conditionally based on user role
  const links: SidebarItem[] = [
    {
      label: 'Dashboard',
      href: '/',
      icon: <HiOutlineHome className='size-5' />,
      shortcut: 'H',
    },
    {
      label: 'Nova Demanda',
      href: '/demands/new',
      icon: <IoAddCircleOutline className='size-5' />,
      shortcut: 'Ctrl+Alt+N',
    },
    {
      label: 'Todas as Demandas',
      href: '/demands',
      icon: <HiOutlineClipboard className='size-5' />,
      shortcut: 'Ctrl+D → L',
    },
    {
      label: 'Calendário',
      href: '/calendar',
      icon: <HiOutlineCalendarDays className='size-5' />,
    },
    {
      label: 'Gestão',
      href: '/gestao/processos',
      icon: <HiOutlineDocumentText className='size-5' />,
      shortcut: 'Ctrl+G → P',
    },
    // Colaboradores - sempre visível, conteúdo filtrado por permissão
    {
      label: 'Colaboradores',
      href: '/collaborators',
      icon: <HiOutlineUserGroup className='size-5' />,
      shortcut: 'C',
    },
    // {
    //   label: 'Componentes',
    //   href: '/components',
    //   icon: <HiCubeTransparent className='size-5' />,
    // },
  ]

  // Função para obter o índice ativo baseado na URL
  const getActiveIndex = () => {
    const activeIndex = links.findIndex(item => {
      if ('type' in item && item.type === 'separator') return false
      const link = item as Links
      if (link.href === '/' && location.pathname === '/') return true
      if (link.href !== '/' && location.pathname.startsWith(link.href)) return true
      return false
    })
    return Math.max(0, activeIndex)
  }

  return (
    <Sidebar open={open} setOpen={setOpen}>
      <SidebarBody className='justify-between gap-10'>
        <div className='flex flex-col flex-1 overflow-y-auto overflow-x-hidden'>
          <Logo />
          <div className='mt-8'>
            <GlassHoverEffect
              activeIndex={getActiveIndex()}
              direction='vertical'
              className='flex flex-col gap-2 relative'
              glassClassName='rounded-xl'
              constrainToContainer={true}
              compactMode={!open}
              compactSize={33}
            >
              {({ containerRef, containerProps, getItemProps }) => (
                <div
                  ref={containerRef as React.RefObject<HTMLDivElement>}
                  className='flex flex-col gap-2 relative'
                  {...containerProps}
                >
                  {links.map((item, idx) => {
                    // if ('type' in item && item.type === 'separator') {
                    //   return <SidebarSeparator key={`separator-${idx}`} label={item.label} />
                    // }
                    return (
                      <SidebarLink key={idx} link={item as Links} itemProps={getItemProps(idx)} />
                    )
                  })}
                </div>
              )}
            </GlassHoverEffect>
          </div>
        </div>

        {/* Footer */}
        <div className='flex flex-col gap-0'>
          {/* Settings Switches */}
          <SidebarSettings />

          {/* Keyboard Shortcuts Help Button - visible only when open */}
          {open && (
            <motion.button
              onClick={showHelpModal}
              title='Atalhos de Teclado (Ctrl+H ou F1)'
              animate={{
                opacity: open ? 1 : 0,
              }}
              transition={{
                duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
                ease: [0.23, 1, 0.32, 1],
                delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.footer : 0,
              }}
              className='flex items-center justify-center gap-2 w-full py-2 text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 border-t border-white/10'
            >
              <HiOutlineCommandLine className='w-4 h-4' />
              <span className='text-xs font-medium'>Atalhos (Ctrl+H)</span>
            </motion.button>
          )}

          {/* Changelog Button - visible only when open */}
          {open && (
            <motion.div
              animate={{
                opacity: open ? 1 : 0,
              }}
              transition={{
                duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
                ease: [0.23, 1, 0.32, 1],
                delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.footer : 0,
              }}
            >
              <NavLink
                to='/changelog'
                title='Histórico de Atualizações'
                className='flex items-center justify-center gap-2 w-full py-2 text-white/90 hover:text-white hover:bg-white/10 transition-all duration-200 border-t border-white/10'
              >
                <HiOutlineNewspaper className='w-4 h-4' />
                <span className='text-xs font-medium'>Atualizações</span>
              </NavLink>
            </motion.div>
          )}

          {/* Version info */}
          <motion.div
            animate={{
              display: open ? 'block' : 'none',
              opacity: open ? 1 : 0,
            }}
            transition={{
              duration: SIDEBAR_ANIMATION_CONFIG.contentDuration,
              ease: [0.23, 1, 0.32, 1],
              type: 'tween',
              delay: open ? SIDEBAR_ANIMATION_CONFIG.delays.version : 0,
            }}
            className='text-xs text-white dark:text-gray-300 text-center py-2 border-t border-gray-200/30 dark:border-neutral-700/30'
          >
            Hermes • 2025
          </motion.div>
        </div>
      </SidebarBody>
    </Sidebar>
  )
}
